import { useEffect, useState } from "react";
import { toast } from "sonner";
import { UserPlus, Pencil } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Switch } from "../components/ui/switch";
import { api, errMsg } from "../lib/api";
import { useLang } from "../lib/i18n";
import { useAuth } from "../lib/auth";
import { PageHeader, Section, Field, StatusBadge, Empty } from "../components/common";
import { fmtDateTime } from "../lib/helpers";
import { DeviceList } from "../components/DeviceList";

export const PERMS = [
  ["view_prices", "See prices & financial info", "Ver precios e información financiera"],
  ["reports", "View reports", "Ver reportes"],
  ["inventory", "Manage inventory", "Administrar inventario"],
  ["employees", "View team list", "Ver lista del equipo"],
  ["activity", "View full activity log", "Ver historial completo"],
  ["settings", "Change settings", "Cambiar configuración"],
  ["ai_assistant", "Use AI assistant", "Usar asistente IA"],
  ["upload_photos", "Upload photos", "Subir fotos"],
  ["add_notes", "Add project notes", "Agregar notas"],
  ["delete_records", "Delete records", "Eliminar registros"],
];
const DEF = {
  office: { reports: false, inventory: true, employees: true, activity: false, settings: false, ai_assistant: true, view_prices: true, upload_photos: true, add_notes: true, delete_records: false },
  crew: { reports: false, inventory: false, employees: false, activity: false, settings: false, ai_assistant: false, view_prices: false, upload_photos: true, add_notes: true, delete_records: false },
};

export default function Team() {
  const { t, lang } = useLang();
  const { user } = useAuth();
  const owner = user.role === "owner";
  const [list, setList] = useState([]);
  const [form, setForm] = useState(null);
  const [acts, setActs] = useState([]);
  const load = () => api.get("/users").then((r) => setList(r.data));
  useEffect(() => { load(); }, []);
  const open = (u) => {
    setForm(u ? { ...u, password: "" } : { name: "", email: "", phone: "", role: "crew", password: "", active: true, permissions: { ...DEF.crew } });
    setActs([]);
    if (u) api.get(`/users/${u.id}/activity`).then((r) => setActs(r.data));
  };
  const save = async (e) => {
    e.preventDefault();
    const body = { ...form, password: form.password || null };
    try { form.id ? await api.put(`/users/${form.id}`, body) : await api.post("/users", body); toast.success(t("Saved", "Guardado")); setForm(null); load(); } catch (err) { toast.error(errMsg(err)); }
  };
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  return (
    <>
      <PageHeader title={t("Team & Permissions", "Equipo y permisos")} subtitle={t("Every person has their own login.", "Cada persona tiene su propio usuario.")}
        actions={owner && <button className="btn-primary" onClick={() => open(null)} data-testid="team-add-user-btn"><UserPlus className="w-5 h-5" /> {t("Add user", "Agregar usuario")}</button>} />
      <Section testid="team-list">
        {!list.length && <Empty text="—" />}
        {list.map((u) => (
          <div key={u.id} className="row-link" data-testid={`team-row-${u.id}`}>
            <span className="grid place-items-center w-10 h-10 rounded-full bg-slate-900 text-white font-bold shrink-0">{u.name[0]}</span>
            <div className="flex-1 min-w-0">
              <div className="font-semibold truncate">{u.name} {!u.active && <span className="text-xs text-red-600">({t("inactive", "inactivo")})</span>}</div>
              <div className="text-xs text-slate-500 truncate">{u.email || u.phone} {owner && `· ${t("last login", "último acceso")}: ${u.last_login ? fmtDateTime(u.last_login, lang) : "—"}`}</div>
            </div>
            <StatusBadge status={u.role} />
            {owner && <button className="btn-ghost btn-sm px-2" onClick={() => open(u)} data-testid={`team-edit-${u.id}`}><Pencil className="w-4 h-4" /></button>}
          </div>
        ))}
      </Section>
      {form && (
        <Dialog open onOpenChange={(o) => !o && setForm(null)}>
          <DialogContent className="max-w-lg max-h-[92vh] overflow-y-auto" data-testid="team-user-dialog">
            <DialogHeader><DialogTitle>{form.id ? t("Edit user", "Editar usuario") : t("Add user", "Agregar usuario")}</DialogTitle></DialogHeader>
            <form onSubmit={save} className="grid gap-3">
              <Field label={t("Name *", "Nombre *")}><input className="inp" required value={form.name} onChange={set("name")} data-testid="team-name-input" /></Field>
              <div className="grid grid-cols-2 gap-3">
                <Field label={t("Email (login) *", "Correo (usuario) *")}><input className="inp" type="email" required value={form.email} onChange={set("email")} data-testid="team-email-input" /></Field>
                <Field label={t("Phone", "Teléfono")}><input className="inp" value={form.phone || ""} onChange={set("phone")} data-testid="team-phone-input" /></Field>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <Field label={t("Role", "Rol")}>
                  <select className="inp" value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value, permissions: e.target.value === "owner" ? {} : { ...DEF[e.target.value] } })} data-testid="team-role-select">
                    <option value="owner">{t("Owner / Admin", "Dueño / Admin")}</option><option value="office">{t("Office / Secretary", "Oficina / Secretaria")}</option><option value="crew">{t("Crew Member", "Miembro del equipo")}</option>
                  </select>
                </Field>
                <Field label={form.id ? t("New password (optional)", "Nueva contraseña (opcional)") : t("Password * (min 8)", "Contraseña * (mín 8)")}><input className="inp" type="text" value={form.password} onChange={set("password")} required={!form.id} minLength={form.id ? 0 : 8} data-testid="team-password-input" /></Field>
              </div>
              <label className="flex items-center justify-between rounded-xl border p-3"><span className="font-semibold">{t("Active", "Activo")}</span><Switch checked={form.active} onCheckedChange={(v) => setForm({ ...form, active: v })} data-testid="team-active-switch" /></label>
              {form.role !== "owner" && (
                <div className="rounded-xl border p-3 grid gap-2">
                  <span className="label">{t("Permissions", "Permisos")}</span>
                  {PERMS.map(([k, en, es]) => (
                    <label key={k} className="flex items-center justify-between min-h-[40px]"><span className="text-sm">{lang === "es" ? es : en}</span>
                      <Switch checked={!!form.permissions?.[k]} onCheckedChange={(v) => setForm({ ...form, permissions: { ...form.permissions, [k]: v } })} data-testid={`team-perm-${k}`} /></label>
                  ))}
                  {form.role === "crew" && <p className="text-xs text-slate-500">{t("Crew members only see projects and appointments assigned to them.", "Los miembros del equipo solo ven proyectos y citas asignados a ellos.")}</p>}
                </div>
              )}
              <button className="btn-primary" data-testid="team-save-btn">{t("Save user", "Guardar usuario")}</button>
            </form>
            {owner && form.id && <div className="mt-2 rounded-xl border" data-testid="team-user-devices"><span className="label block px-4 pt-3">{t("Devices", "Dispositivos")}</span><DeviceList uid={form.id} /></div>}
            {acts.length > 0 && <div className="mt-2"><span className="label">{t("Recent activity", "Actividad reciente")}</span>{acts.slice(0, 8).map((a) => <div key={a.id} className="text-xs py-1 border-b"><span className="mono text-slate-400">{fmtDateTime(a.created_at, lang)}</span> {a.action}</div>)}</div>}
          </DialogContent>
        </Dialog>
      )}
    </>
  );
}
